import React, { useRef } from "react";

interface ImageBreakProps {
  image: string;
  alt: string;
  eyebrow?: string;
  title?: string;
}

const ImageBreak = ({ image, alt, eyebrow, title }: ImageBreakProps) => {
  const sectionRef = useRef<HTMLElement>(null);

  // Lightweight parallax on scroll (rAF-throttled, passive)
  const onScroll = () => {
    const el = sectionRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || 1;
    if (rect.bottom < 0 || rect.top > vh) return;
    const progress = (rect.top + rect.height / 2 - vh / 2) / vh; // -1..1
    const img = el.querySelector<HTMLImageElement>("[data-parallax]");
    if (img)
      img.style.transform = `translateY(${progress * -40}px) scale(1.12)`;
  };

  React.useEffect(() => {
    let ticking = false;
    const handler = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          onScroll();
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handler, { passive: true });
    handler();
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full h-[55vh] min-h-[380px] overflow-hidden"
    >
      <img
        data-parallax
        src={image}
        alt={alt}
        className="absolute inset-0 w-full h-[120%] object-cover will-change-transform"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-black/45" />
      <div className="absolute inset-0 bg-grid opacity-40" />
      <div className="absolute inset-0 flex items-end justify-center pb-12 md:pb-16">
        <div className="text-center px-4 max-w-2xl">
          {eyebrow && (
            <span className="block text-xs font-bold uppercase tracking-[0.3em] text-primary mb-3">
              {eyebrow}
            </span>
          )}
          {title && (
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-white leading-tight">
              {title}
            </h2>
          )}
        </div>
      </div>
    </section>
  );
};

export default ImageBreak;
